import PspSelect from "@/components/pspSelect/pspSelect";
import { translate, TranslatorKeys } from "@/config/translator";
import {
  Button,
  Dialog,
  DialogSurface,
  MessageBar,
  Switch,
} from "@fluentui/react-components";
import { createPortal } from "react-dom";
import { useUploadDialog } from "./uploadDialog/useUploadDialog";
import { PspTextArea } from "@/components/pspTextArea/pspTextArea";
import { FileUploadArea } from "@/components/fileDropArea/fileUploadArea";
import { SelectedFilesList } from "@/components/selectedFilesList/selectedFilesList";
import { PspRadioGroup } from "@/components/pspRadioGroup/pspRadioGroup";
import { PspAvatar } from "@/components/pspAvatar/pspAvatar";
import { beautifyBytes } from "@/config/utils";

export declare type UploadDialogProps = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  fetchFiles: () => void;
};

export default function UploadDialog({
  isOpen,
  setIsOpen,
  fetchFiles,
}: UploadDialogProps) {
  const {
    onUpload,
    onFilesChange,
    lang,
    selectedFiles,
    documentType,
    setDocumentType,
    onClose,
    selectedMandate,
    onChangeWantsNote,
    noteState,
    onChangeNote,
    uploadButtonDisabled,
    setSelectedFiles,
    payrollMonth,
    setPayrollMonth,
    extraYear,
    months,
    isDataInvalid,
    documentTypeOptions,
    fileTooBig,
    uploadLimits,
    willBeSentToName,
  } = useUploadDialog({ fetchFiles, setIsOpen });

  return createPortal(
    <Dialog open={isOpen}>
      <DialogSurface>
        {/* HEADER */}
        <div className="flex flex-col">
          <div className="text-xl font-medium">
            {translate("uploadFile", lang)}
          </div>
          <div>
            <span className="text-font-secondary">
              {translate("forLowercase", lang)}{" "}
            </span>
            <span className="text-primary font-medium">
              {selectedMandate?.name}
            </span>
          </div>
        </div>

        <div className="h-px w-full -mx-3 bg-neutral my-3" />

        {/* DOCUMENT TYPE SELECTION */}
        {documentTypeOptions && (
          <PspRadioGroup
            hasBorder
            label={translate("selectDocumentType", lang)}
            layout="horizontal"
            options={documentTypeOptions}
            onChange={setDocumentType}
            value={documentType}
          />
        )}

        {/* WILL BE SENT TO */}
        {willBeSentToName && selectedMandate && (
          <>
            <div className="flex flex-row gap-2 items-center pt-2">
              <PspAvatar name={willBeSentToName} size="small" />
              <div className="text-base">
                <span className="text-font-secondary">
                  {translate("willBeSentTo", lang)}{" "}
                </span>
                <span className="text-font-black font-medium">
                  {willBeSentToName}
                </span>
              </div>
            </div>

            {/* SELECTED FILES LIST + FILE UPLOAD */}
            <div className="my-6">
              <div className="text-font-secondary mb-3">
                {selectedFiles && selectedFiles.length !== 0
                  ? translate("uploadedFiles", lang) +
                    ` (${selectedFiles.length})`
                  : translate("uploadFiles", lang)}
              </div>
              {selectedFiles && selectedFiles.length !== 0 && (
                <div className="mb-4 max-h-50 overflow-y-auto overflow-x-visible -mx-3 px-3">
                  <SelectedFilesList
                    selectedFiles={selectedFiles}
                    setSelectedFiles={setSelectedFiles}
                    uploadLimits={uploadLimits}
                  />
                  {fileTooBig === true && (
                    <span className="text-red-800">
                      {translate("allFilesTooBig", lang, {
                        "[bytes]": beautifyBytes(
                          uploadLimits?.maxRequestSizeBytes ?? 0,
                        ),
                      })}
                    </span>
                  )}
                </div>
              )}
              <div className="flex flex-col gap-2">
                <FileUploadArea onChange={onFilesChange} />
                <MessageBar>{translate("nameFilesClearly", lang)}</MessageBar>
              </div>
            </div>
          </>
        )}

        <div className="flex flex-col gap-4">
          {/* PAYROLL MONTH */}
          {documentType === "payroll_accounting" && (
            <PspSelect
              onChange={setPayrollMonth}
              labelString={translate("month", lang)}
              value={payrollMonth}
            >
              <>
                <option value="" disabled>
                  {translate("selectOption", lang)}
                </option>
                {/* Normal rolling months */}
                {months.map(({ month, year }) => (
                  <option key={`m-${month}-${year}`} value={month}>
                    {translate(`month${month}` as TranslatorKeys, lang)}, {year}
                  </option>
                ))}

                {/* Special accounting months 13–15 */}
                {selectedMandate &&
                  Array.from({
                    length: (selectedMandate.anzahlAbrechnungen ?? 15) - 12,
                  }).map((_, i) => {
                    const value = 12 + i; // 12, 13, 14
                    return (
                      <option key={`extra-${value}`} value={value}>
                        {translate(`month${value}` as TranslatorKeys, lang)},{" "}
                        {extraYear}
                      </option>
                    );
                  })}
              </>
            </PspSelect>
          )}

          {/* WANTS NOTE */}
          {selectedFiles && selectedFiles.length !== 0 && (
            <>
              <Switch
                label={translate("addNote", lang)}
                checked={noteState.wantsNote}
                onChange={(_, data) => onChangeWantsNote(data.checked)}
              />

              {/* NOTE */}
              {noteState.wantsNote && (
                <div className="flex flex-col gap-2">
                  <PspTextArea
                    value={noteState.note}
                    label={"note"}
                    onChange={onChangeNote}
                  />
                  <p className="text-xs text-font-secondary italic">
                    {translate("noteWarning", lang)}
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        {/* ACTIONS */}
        <div className="mt-6 flex flex-row justify-end gap-2">
          <Button appearance="secondary" onClick={onClose}>
            {translate("cancel", lang)}
          </Button>
          {selectedFiles && selectedFiles.length !== 0 && (
            <>
              <Button
                appearance="primary"
                disabled={uploadButtonDisabled || isDataInvalid}
                onClick={onUpload}
              >
                {translate("upload", lang)}
              </Button>
            </>
          )}
        </div>
      </DialogSurface>
    </Dialog>,
    document.getElementById("portal-root")!,
  );
}
